import {
  TableColumnID,
  TableColumnLabel,
  columnSortingMapByView,
  tableColumnIdToTableConfigMap,
  tableConfigToTableColumnIdAndLabelMap,
} from 'src/app/entities/products/enums/table-column-ids.enum';
import {
  ITableColumn,
  ITableColumnConfig,
  TableConfigColumns,
} from 'src/app/entities/products/interfaces/table-column.interface';
import { CountryCode } from '@app-shared/enums/country-codes.enum';
import { IAuthUser } from '@app-core/interfaces/auth-user.interface';
import { UserStatus } from '@app-shared/enums/user-statuses.enum';

import { BackendViewNames } from '../enums/product-views.enum';

/**
 * Returns an array of enabled table column IDs
 *
 * @param tableColumns
 */
export function getActiveTableColumns(
  tableColumns: ITableColumn[],
): TableColumnID[] {
  return (
    tableColumns
      .map(c => {
        if (c.enabled) return c.id;
        else return undefined;
      })
      // note: `is` is used here to "fix" typescript not knowing that we filtered the falsy values
      .filter((c): c is TableColumnID => !!c)
  );
}

export function parseTableColumnConfig(
  tableColumnConfig: ITableColumnConfig[],
  view: ITableColumnConfig['tab_name'],
  countryCode: CountryCode,
  user?: IAuthUser | null,
): ITableColumn[] {
  let tableColumns: ITableColumn[] = [];
  const config: ITableColumnConfig | undefined = tableColumnConfig.find(
    c => c.tab_name === view,
  );

  if (!config) {
    // eslint-disable-next-line no-console
    console.error('Config not found, TableColumnConfig probably empty');

    return tableColumns;
  }

  // loop over the config and add columns that need to be in this view
  Object.entries(config)
    // @ts-ignore No idea what the type problem is here so ignoring for now
    .filter(([key, _]: [keyof ITableColumnConfig, boolean]) => {
      return key !== 'id' && key !== 'tab_name';
    })
    // @ts-ignore No idea what the type problem is here so ignoring for now
    .forEach(([key, value]: [TableConfigColumns, boolean]) => {
      const [columnID, columnLabel] =
        tableConfigToTableColumnIdAndLabelMap.get(key);

      if (columnID && columnLabel) {
        tableColumns.push({
          id: columnID,
          label: columnLabel,
          enabled: !!value,
        });
      }
    });

  // filter out the columns that are not supposed to be shown for this view and country
  tableColumns = tableColumns.filter(c => {
    const idToCheck = columnSortingMapByView
      .get(view)!
      .get(countryCode)
      .get(c.id);

    return !!idToCheck || idToCheck === 0;
  });

  tableColumns.sort((c1, c2) => {
    const orderOfC1 = columnSortingMapByView
      .get(view)!
      .get(countryCode)
      .get(c1.id);
    const orderOfC2 = columnSortingMapByView
      .get(view)!
      .get(countryCode)
      .get(c2.id);

    return orderOfC1! - orderOfC2!;
  });

  // Handle VIP columns for NOVELTIES view
  if (
    view === BackendViewNames.NOVELTIES &&
    user?.statuses?.includes(UserStatus.VIP)
  ) {
    // Get config keys for VIP columns
    const vipStockoutKey = Array.from(
      tableConfigToTableColumnIdAndLabelMap.entries(),
    ).find(([_, [id]]) => id === TableColumnID.VipExpectedStockOutDate)?.[0];
    const vipAvailabilityKey = Array.from(
      tableConfigToTableColumnIdAndLabelMap.entries(),
    ).find(
      ([_, [id]]) => id === TableColumnID.VipExpectedAvailabilityDate,
    )?.[0];

    // Add VIP Expected Stockout Date column if not already present
    if (
      !tableColumns.some(
        col => col.id === TableColumnID.VipExpectedStockOutDate,
      )
    ) {
      tableColumns.push({
        id: TableColumnID.VipExpectedStockOutDate,
        label: TableColumnLabel.VipExpectedStockOutDate,
        enabled: vipStockoutKey ? !!config[vipStockoutKey] : true, // Use config value if exists, otherwise default to true
      });
    }

    // Add VIP Expected Availability Date column if not already present
    if (
      !tableColumns.some(
        col => col.id === TableColumnID.VipExpectedAvailabilityDate,
      )
    ) {
      tableColumns.push({
        id: TableColumnID.VipExpectedAvailabilityDate,
        label: TableColumnLabel.VipExpectedAvailabilityDate,
        enabled: vipAvailabilityKey ? !!config[vipAvailabilityKey] : true, // Use config value if exists, otherwise default to true
      });
    }
  }

  // since this column is not set in the backend, we need to always add it if the chosen view is `orders`
  if (view === 'orders') {
    tableColumns.unshift({
      id: TableColumnID.Checkbox,
      label: TableColumnLabel.Checkbox,
      enabled: true,
      doNotShow: true,
    });
  }

  return tableColumns;
}

export function makeTableColumnConfigFromTableColumns(
  tableColumns: ITableColumn[],
): Omit<ITableColumnConfig, 'id' | 'tab_name'> {
  return {
    ...tableColumns.reduce(
      (prev, c) => {
        const key = tableColumnIdToTableConfigMap.get(c.id);

        if (!key) {
          return { ...prev };
        }

        return {
          ...prev,
          [key]: c.enabled,
        };
      },
      {} as Omit<ITableColumnConfig, 'id' | 'tab_name'>,
    ),
  };
}
